/* eslint-disable */
import io from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.currentToken = null;
    this.listeners = {};
    this.pendingEvents = [];
    this.connectionAttempt = false;
    this.reconnectTimer = null;
    
    // Bind methods
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.setupListeners = this.setupListeners.bind(this);
    this.emit = this.emit.bind(this);
    this.on = this.on.bind(this);
    this.off = this.off.bind(this);
  }

  connect() {
    // Clear any pending reconnect
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Prevent multiple rapid connection attempts
    if (this.connectionAttempt) {
      console.log('⚠️ Connection attempt already in progress');
      // Expose socket globally for debugging
      if (typeof window !== "undefined") {
        window._debugSocket = this.socket;
        console.log("🔧 Socket exposed globally for debugging");
      }

      return this.socket;
    }
    
    // If already connected, return existing socket
    if (this.socket && this.socket.connected) {
      console.log('✅ Socket already connected:', this.socket.id);
      // Expose socket globally for debugging
      if (typeof window !== "undefined") {
        window._debugSocket = this.socket;
        console.log("🔧 Socket exposed globally for debugging");
      }

      return this.socket;
    }
    
    // Disconnect existing socket if it exists but not connected
    if (this.socket) {
      console.log('⚠️ Cleaning up stale socket');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('❌ No token found for socket connection');
      return null;
    }

    console.log('🔌 Attempting socket connection...');
    this.connectionAttempt = true;
    
    try {
      // Clean token (remove quotes if present)
      const cleanToken = token.replace(/"/g, '').trim();
      
      // Get the base URL
      const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
      
      this.socket = io(baseUrl, {
        auth: { token: cleanToken },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        timeout: 20000,
        forceNew: true
      });

      this.setupListeners();
      
      // Clear connection attempt flag when connection is established
      this.socket.once('connect', () => {
        this.connectionAttempt = false;
        this.isConnected = true;
        console.log('✅ Socket connected successfully:', this.socket.id);
      });
      
      this.socket.once('connect_error', (error) => {
        this.connectionAttempt = false;
        console.error('❌ Socket connection error:', error);
      });
      
      // Expose socket globally for debugging
      if (typeof window !== "undefined") {
        window._debugSocket = this.socket;
        console.log("🔧 Socket exposed globally for debugging");
      }

      return this.socket;
    } catch (error) {
      console.error('❌ Socket connection error:', error);
      this.connectionAttempt = false;
      return null;
    }
  }

  disconnect() {
    console.log('🔌 Manually disconnecting socket...');
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.connectionAttempt = false;
    this.isConnected = false;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  setupListeners() {
    if (!this.socket) return;
    
    this.socket.on('connect', () => {
      this.isConnected = true;
      console.log('✅ Socket connected:', this.socket.id);
      
      // Process pending events
      if (this.pendingEvents.length > 0) {
        console.log('📡 Processing pending events:', this.pendingEvents.length);
        this.pendingEvents.forEach(({ event, data }) => {
          this.socket.emit(event, data);
        });
        this.pendingEvents = [];
      }
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      console.log('❌ Socket disconnected:', reason);
    });

    this.socket.on('error', (error) => {
      console.error('❌ Socket error:', error);
    });
  }

  emit(event, data) {
    if (!this.socket || !this.socket.connected) {
      console.log(`📦 Queuing event ${event} - socket not connected`);
      this.pendingEvents.push({ event, data });
      return false;
    }
    
    console.log(`📡 Emitting event: ${event}`, data);
    this.socket.emit(event, data);
    return true;
  }

  on(event, callback) {
    if (!this.socket) {
      console.log(`🎯 Setting up ${event} listener (socket not ready)`);
      this.listeners[event] = callback;
      return;
    }
    
    console.log(`🎯 Setting up ${event} listener`);
    this.socket.on(event, callback);
  }

  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  // Call event listeners
  onIncomingCall(callback) {
    console.log('📞 Setting up incomingCall listener');
    this.on('incomingCall', callback);
  }

  onCallAnswered(callback) {
    console.log('📞 Setting up callAnswered listener');
    this.on('callAnswered', callback);
  }

  onCallConnected(callback) {
    console.log('📞 Setting up callConnected listener');
    this.on('callConnected', callback);
  }

  onCallEnded(callback) {
    console.log('📞 Setting up callEnded listener');
    this.on('callEnded', callback);
  }

  onICECandidate(callback) {
    console.log('❄️ Setting up iceCandidate listener');
    this.on('iceCandidate', callback);
  }

  onCallRejected(callback) {
    console.log('📞 Setting up callRejected listener');
    this.on('callRejected', callback);
  }

  onCallAccepted(callback) {
    console.log('📞 Setting up callAccepted listener');
    this.on('callAccepted', callback);
  }

  onCallDeclined(callback) {
    console.log('📞 Setting up callDeclined listener');
    this.on('callDeclined', callback);
  }

  onCallMissed(callback) {
    console.log('📞 Setting up callMissed listener');
    this.on('callMissed', callback);
  }

  onPeerConnection(callback) {
    console.log('🔗 Setting up peerConnection listener');
    this.on('peerConnection', callback);
  }

  onRemoteStream(callback) {
    console.log('📹 Setting up remoteStream listener');
    this.on('remoteStream', callback);
  }

  onLocalStream(callback) {
    console.log('📹 Setting up localStream listener');
    this.on('localStream', callback);
  }

  onCallError(callback) {
    console.log('❌ Setting up callError listener');
    this.on('callError', callback);
  }

  // Off methods for cleanup
  offIncomingCall() {
    console.log('🧹 Cleaning up incomingCall listener');
    this.off('incomingCall');
  }

  offCallAnswered() {
    console.log('🧹 Cleaning up callAnswered listener');
    this.off('callAnswered');
  }

  offCallConnected() {
    console.log('🧹 Cleaning up callConnected listener');
    this.off('callConnected');
  }

  offCallEnded() {
    console.log('🧹 Cleaning up callEnded listener');
    this.off('callEnded');
  }

  offICECandidate() {
    console.log('🧹 Cleaning up iceCandidate listener');
    this.off('iceCandidate');
  }

  offCallRejected() {
    console.log('🧹 Cleaning up callRejected listener');
    this.off('callRejected');
  }

  offCallAccepted() {
    console.log('🧹 Cleaning up callAccepted listener');
    this.off('callAccepted');
  }

  offCallDeclined() {
    console.log('🧹 Cleaning up callDeclined listener');
    this.off('callDeclined');
  }

  offCallMissed() {
    console.log('🧹 Cleaning up callMissed listener');
    this.off('callMissed');
  }

  offPeerConnection() {
    console.log('🧹 Cleaning up peerConnection listener');
    this.off('peerConnection');
  }

  offRemoteStream() {
    console.log('🧹 Cleaning up remoteStream listener');
    this.off('remoteStream');
  }

  offLocalStream() {
    console.log('🧹 Cleaning up localStream listener');
    this.off('localStream');
  }

  offCallError() {
    console.log('🧹 Cleaning up callError listener');
    this.off('callError');
  }


  // Join user room
  joinUser(userId) {
    console.log('👤 Joining user room:', userId);
    if (this.socket && this.socket.connected) {
      this.socket.emit('joinUser', userId);
    } else {
      console.log('⚠️ Socket not connected, storing joinUser for later');
      this.pendingEvents.push({ event: 'joinUser', data: userId });
    }
  }

  // Message event listeners
  onNewMessage(callback) {
    console.log('📨 Setting up newMessage listener');
    this.on('newMessage', callback);
  }

  offNewMessage() {
    console.log('🧹 Cleaning up newMessage listener');
    this.off('newMessage');
  }

  // Conversation event listeners
  onConversationUpdated(callback) {
    console.log('💬 Setting up conversationUpdated listener');
    this.on('conversationUpdated', callback);
  }

  offConversationUpdated() {
    console.log('🧹 Cleaning up conversationUpdated listener');
    this.off('conversationUpdated');
  }

  // Offer event listeners
  onOfferUpdated(callback) {
    console.log('💰 Setting up offerUpdated listener');
    this.on('offerUpdated', callback);
  }

  offOfferUpdated() {
    console.log('🧹 Cleaning up offerUpdated listener');
    this.off('offerUpdated');
  }

  // Typing indicator listeners
  onUserTyping(callback) {
    console.log('⌨️ Setting up userTyping listener');
    this.on('userTyping', callback);
  }

  offUserTyping() {
    console.log('🧹 Cleaning up userTyping listener');
    this.off('userTyping');
  }

  // New offer event listeners
  onNewOffer(callback) {
    console.log('💰 Setting up newOffer listener');
    this.on('newOffer', callback);
  }

  offNewOffer() {
    console.log('🧹 Cleaning up newOffer listener');
    this.off('newOffer');
  }

  // Call emission methods
  emitICECandidate(data) {
    console.log('❄️ Emitting iceCandidate event:', data);
    this.emit('iceCandidate', data);
  }

  emitCallAnswer(data) {
    console.log('📞 Emitting callAnswer event:', data);
    this.emit('callAnswer', data);
  }

  emitEndCall(data) {
    console.log('📞 Emitting endCall event:', data);
    this.emit('endCall', data);
  }

  emitStartCall(data) {
    console.log('📞 Emitting startCall event:', data);
    this.emit('startCall', data);
  }

  emitRejectCall(data) {
    console.log('📞 Emitting rejectCall event:', data);
    this.emit('rejectCall', data);
  }

  emitAcceptCall(data) {
    console.log('📞 Emitting acceptCall event:', data);
    this.emit('acceptCall', data);
  }

}

// Create singleton instance
const socketService = new SocketService();
export default socketService;